import _isObject from 'lodash/isObject';
import _isEqual from 'lodash/isEqual';
import _isEmpty from 'lodash/isEmpty';
import _transform from 'lodash/transform';

// State
import { exportableStore as store } from '../state/store';

// Utils
import isomorphicCookies from './isomorphicCookies';
import parseISO from './parseISO';

const locale = isomorphicCookies().get('locale');
const naming =
  locale === 'ru'
    ? { flat: 'кв.', floor: 'этаж', porch: 'подъезд', intercom: 'домофон' }
    : { flat: 'flat', floor: 'floor', porch: 'porch', intercom: 'intercom' };

/**
 * Format numbers (prices etc)
 * @param {number|string} value: Unformatted number
 * @param {FormatNumbersOptions} options
 * @return {string} Formatted number string
 */
export const formatNumber = (value, { digits = 0 } = { digits: 0 }) => {
  const processedValue = typeof value === 'string' ? value.replace(',', '') : value;
  const rounded = parseFloat(processedValue).toFixed(digits);
  const parts = String(rounded).split('.');
  parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ' ');

  if (digits === 0) {
    return parts[0] !== 'NaN' ? parts[0] : '-';
  }

  return parts[1] && Number(parts[1]) === 0 ? parts[0] : parts.join('.');
};
export const addSpace = nStr => {
  nStr += '';
  let x = nStr.split('.');
  let x1 = x[0];
  let x2 = x.length > 1 ? '.' + x[1] : '';
  const rgx = /(\d+)(\d{3})/;
  while (rgx.test(x1)) {
    x1 = x1.replace(rgx, '$1' + ' ' + '$2');
  }
  return x1 + x2;
};
// формируем address_in_line
export const formatMinAddress = address => {
  const { street_type, street, house, housing, housing_type, city_id } = address;
  const { userCity, list } = store.getState().city;

  // при нажатии на сохраненный адрес есть city_id и он не обязательно равен текущему городу
  const currentCityId = city_id || userCity.id;
  const currentCityName = list.find(city => city.id === currentCityId)?.name;

  const cityString = currentCityName ? `${currentCityName}, ` : '';
  const streetTypeString = street_type ? `${street_type} ` : '';
  const streetString = street ? `${street}` : '';
  const houseString = house ? `, ${house}` : '';
  const housingTypeString = housing_type ? `, ${housing_type} ` : '';
  const housingString = housing ? `${housing}` : '';

  return `${cityString}${streetTypeString}${streetString}${houseString}${housingTypeString}${housingString}`;
};

// формируем полный адрес
export const formatMaxAddress = address => {
  const { address_in_line, flat, floor, porch, intercom } = address;

  const flatString = flat ? `, ${naming.flat} ${flat}` : '';
  const floorString = floor ? `, ${naming.floor} ${floor}` : '';
  const porchString = porch ? `, ${naming.porch} ${porch}` : '';
  const intercomString = intercom ? `, ${naming.intercom} ${intercom}` : '';

  return `${address_in_line}${flatString}${floorString}${porchString}${intercomString}`;
};

// ул. Кравченко, 8 с. 6 к. 1'
// нужен, чтобы отображать список сохраненных адресов
export const formatSavedAddress = address => {
  return formatMaxAddress({ ...address, address_in_line: formatMinAddress(address) });
};

/**
 * Deep diff between two object, using lodash
 * @param  {Object} object Object compared
 * @param  {Object} base   Object to compare with
 * @return {Object}        Return a new object who represent the diff
 */
export function difference(object, base) {
  function changes(object, base) {
    return _transform(object, (result, value, key) => {
      if (!_isEqual(value, base[key])) {
        result[key] = _isObject(value) && _isObject(base[key]) ? changes(value, base[key]) : value;
      }
    });
  }
  return changes(object, base);
}

/**
 * Check if string date is valid Date
 * @param  {String} dateString  input string date
 * @return {Boolean}            Returns a test result
 */
export function isValidDate(dateString) {
  if (_isEmpty(dateString)) {
    return false;
  }

  const date = new Date(dateString);

  if (date instanceof Date && !isNaN(date) && date.getFullYear() > 1) {
    return true;
  }

  return false;
}

export const isToday = someDate => {
  const parsedDate = typeof someDate === 'string' ? parseISO(someDate) : new Date(someDate);
  const equalDate = typeof someDate === 'object' ? someDate : parsedDate;
  const today = new Date();

  return (
    equalDate.getDate() === today.getDate() &&
    equalDate.getMonth() === today.getMonth() &&
    equalDate.getFullYear() === today.getFullYear()
  );
};

export const objectsDiff = (a, b) => {
  const diff = {};

  Object.keys(b).forEach(key => {
    if (a[key] !== b[key]) {
      diff[key] = b[key];
    }
  });

  return diff;
};

export const objectsDiffBoolean = (a, b) => {
  let diff = false;

  Object.keys(b).forEach(key => {
    if (!diff && a[key] !== b[key]) {
      diff = true;
    }
  });

  return diff;
};

export const retryWithDelay = async (fn, retries = 2, delay = 500) => {
  let attempt = 0;

  while (attempt < retries) {
    try {
      return await fn();
    } catch (error) {
      attempt += 1;
      if (attempt < retries) {
        await new Promise(resolve => setTimeout(resolve, delay));
      }
    }
  }

  return null;
};
